import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  AccountTree as MapIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

function n(v) {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
}

function okrCategoryColor(category) {
  const s = category?.theme_color && String(category.theme_color).trim();
  return s || undefined;
}

function MapFlowCard({ title, subtitle, children, accent }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minWidth: 200,
        maxWidth: 360,
        flex: '1 1 200px',
        border: 2,
        borderColor: accent || 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle != null && subtitle !== '' && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

function reportingManagerIdForRow(row) {
  return row.reporting_manager_contact_id || row.Contact?.reporting_manager_contact_id || null;
}

/** Build roots + adjacency: manager contact_id → direct reports (same company bucket only). */
function buildReportingTreeIndex(rows) {
  const withProfile = rows.filter((r) => !r.needsEmployeeProfile);
  const pending = [...rows.filter((r) => r.needsEmployeeProfile)].sort((a, b) => {
    const an = `${a.Contact?.last_name || ''} ${a.Contact?.first_name || ''}`.trim().toLowerCase();
    const bn = `${b.Contact?.last_name || ''} ${b.Contact?.first_name || ''}`.trim().toLowerCase();
    return an.localeCompare(bn);
  });
  const contactIds = new Set(withProfile.map((r) => r.contact_id));

  const childrenByManagerContactId = new Map();
  for (const r of withProfile) {
    const mgr = reportingManagerIdForRow(r);
    const key = mgr && contactIds.has(mgr) ? mgr : '__ROOT__';
    if (!childrenByManagerContactId.has(key)) childrenByManagerContactId.set(key, []);
    childrenByManagerContactId.get(key).push(r);
  }
  for (const [, list] of childrenByManagerContactId) {
    list.sort((a, b) => {
      const an = `${a.Contact?.last_name || ''} ${a.Contact?.first_name || ''}`.trim().toLowerCase();
      const bn = `${b.Contact?.last_name || ''} ${b.Contact?.first_name || ''}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
  }
  const roots = childrenByManagerContactId.get('__ROOT__') || [];
  return { roots, childrenByManagerContactId, pending };
}

function directoryRowSubtitle(row) {
  const c = row?.Contact;
  const roleN = c?.employeeJobRole?.name;
  const lvl = c?.employeeTitle?.name;
  if (roleN || lvl) return [roleN, lvl].filter(Boolean).join(' · ');
  if (c?.employeeOfferedTitle?.name) return c.employeeOfferedTitle.name;
  return row?.employee_code || row?.job_title || '—';
}

const MY_MAP_ONLY_OTHERS_MSG =
  'In My Employee Map you can browse the company tree, but you may only open your own employee record and OKR map.';

function rowCollapseKey(row) {
  return String(row.contact_id != null ? row.contact_id : row.id);
}

function OkrCompanyReportingTree({ rows, selectedId, onSelectRow, maxDepth = 24, restrictSelectToEmployeeId }) {
  const { roots, childrenByManagerContactId, pending } = buildReportingTreeIndex(rows);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());

  const toggleCollapsed = useCallback((key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const rowSelectDisabled = (row) =>
    restrictSelectToEmployeeId != null &&
    !row.needsEmployeeProfile &&
    String(row.id) !== String(restrictSelectToEmployeeId);

  const renderBranch = (row, depth) => {
    const c = row.Contact;
    const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '—';
    const sel = String(row.id) === String(selectedId);
    const kids = depth >= maxDepth ? [] : childrenByManagerContactId.get(row.contact_id) || [];
    const disabled = rowSelectDisabled(row);
    const ck = rowCollapseKey(row);
    const hasBranch = kids.length > 0;
    const collapsed = collapsedKeys.has(ck);

    return (
      <Box key={row.id} sx={{ py: 0.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box
            sx={{
              width: 32,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              pt: 0.25,
            }}
          >
            {hasBranch ? (
              <IconButton
                aria-label={collapsed ? 'Expand' : 'Collapse'}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollapsed(ck);
                }}
                sx={{ p: 0.25 }}
              >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28, height: 28 }} aria-hidden />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={disabled ? MY_MAP_ONLY_OTHERS_MSG : ''} placement="right" enterDelay={400}>
              <span>
                <ListItemButton
                  selected={sel}
                  disabled={disabled}
                  onClick={() => onSelectRow(row)}
                  sx={{ borderRadius: 1, py: 0.25, alignItems: 'flex-start' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                      {c?.first_name?.[0]}
                      {c?.last_name?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<span>{name}</span>}
                    secondary={directoryRowSubtitle(row)}
                    primaryTypographyProps={{ noWrap: false, fontWeight: sel ? 600 : 500 }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {hasBranch && !collapsed ? (
          <Box
            sx={(theme) => ({
              ml: 2,
              pl: 2,
              mt: 0.25,
              borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            })}
          >
            {kids.map((child) => renderBranch(child, depth + 1))}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box>
      {roots.length === 0 && pending.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
          No people in this company for the current search.
        </Typography>
      ) : (
        roots.map((r) => renderBranch(r, 0))
      )}
      {pending.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: 'block', mt: 1, px: 1, fontWeight: 600 }}
          >
            Needs HR profile (shown flat; not linked in the tree yet)
          </Typography>
          {pending.map((row) => {
            const c = row.Contact;
            const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '—';
            const sel = String(row.id) === String(selectedId);
            const disabled = restrictSelectToEmployeeId != null;
            return (
              <Tooltip
                key={row.id}
                title={
                  disabled
                    ? 'People without an HR profile cannot be opened from My Employee Map. Ask an administrator if you need a profile.'
                    : ''
                }
                placement="right"
                enterDelay={400}
              >
                <span>
                  <ListItemButton
                    selected={sel}
                    disabled={disabled}
                    onClick={() => onSelectRow(row)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                        {c?.first_name?.[0]}
                        {c?.last_name?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>{name}</span>
                          <Chip size="small" label="No HR profile" color="warning" variant="outlined" sx={{ height: 20 }} />
                        </Box>
                      }
                      secondary={c?.email || '—'}
                    />
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
        </>
      )}
    </Box>
  );
}

export default function OkrEmployeeMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const selfOnlyBrowse = useMemo(
    () => location.pathname.startsWith('/my-items/employee-map') && !isAdmin,
    [location.pathname, isAdmin]
  );

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [myEmployeeId, setMyEmployeeId] = useState(null);

  useEffect(() => {
    const e = user?.Employee || user?.employee;
    setMyEmployeeId(e?.id ? String(e.id) : null);
  }, [user?.Employee?.id, user?.employee?.id]);

  useEffect(() => {
    if (!selfOnlyBrowse) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/auth/profile');
        if (cancelled) return;
        const d = r.data?.data;
        const e = d?.Employee || d?.employee;
        if (e?.id) setMyEmployeeId(String(e.id));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selfOnlyBrowse]);

  useEffect(() => {
    if (selfOnlyBrowse) setSearch('');
  }, [selfOnlyBrowse]);

  const loadEmployees = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/employees/directory-for-okr', {
        params: {
          page: 1,
          limit: 500,
          search: selfOnlyBrowse ? undefined : search.trim() || undefined,
        },
      });
      const rows = r.data?.data || [];
      setEmployees(rows);
    } catch {
      toast.error('Failed to load employees');
      setEmployees([]);
    } finally {
      setLoadingList(false);
    }
  }, [search, selfOnlyBrowse]);

  useEffect(() => {
    const t = setTimeout(() => loadEmployees(), 300);
    return () => clearTimeout(t);
  }, [loadEmployees]);

  const loadMap = useCallback(async (employeeId) => {
    if (!employeeId) return;
    setMapLoading(true);
    setMapData(null);
    try {
      const r = await api.get(`/okr/employee-map/${employeeId}`);
      setMapData(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load OKR map');
      setMapData(null);
    } finally {
      setMapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId || String(selectedId).startsWith('pending:')) return;
    loadMap(selectedId);
  }, [selectedId, loadMap]);

  useEffect(() => {
    setCatalogCategoryFilter('');
  }, [selectedId]);

  useEffect(() => {
    if (!selfOnlyBrowse || !myEmployeeId || loadingList) return;
    const exists = employees.some((e) => String(e.id) === String(myEmployeeId));
    if (exists) setSelectedId((sid) => sid || myEmployeeId);
  }, [selfOnlyBrowse, myEmployeeId, employees, loadingList]);

  const companyTree = useMemo(() => {
    const m = new Map();
    for (const row of employees) {
      const label = row.company_display || row.company_name || 'No company assigned';
      if (!m.has(label)) m.set(label, []);
      m.get(label).push(row);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [employees]);

  const handleSelectEmployee = (row) => {
    if (row.needsEmployeeProfile) {
      if (selfOnlyBrowse) {
        toast.info(
          'This person does not have an HR employee profile yet. In My Employee Map you can only open your own record once your account is linked to an employee profile.'
        );
        return;
      }
      if (isAdmin) {
        toast.info('Add an HR profile for this contact to use the OKR map.');
        navigate(`/employees/new?contact_id=${row.contact_id}`);
      } else {
        toast.info('This person needs an employee HR profile. Ask an administrator to add it under Administration → Employees.');
      }
      return;
    }
    if (selfOnlyBrowse) {
      if (!myEmployeeId) {
        toast.warning(
          'Your login is not linked to an employee HR record, so your map cannot be opened. You can still browse the company directory. Ask an administrator to link your user to your employee profile.'
        );
        return;
      }
      if (String(row.id) !== String(myEmployeeId)) {
        toast.warning(MY_MAP_ONLY_OTHERS_MSG);
        return;
      }
    }
    setSelectedId(row.id);
  };

  const selectedRow = useMemo(
    () => employees.find((e) => String(e.id) === String(selectedId)),
    [employees, selectedId]
  );

  const emp = mapData?.employee;
  const contact = emp?.Contact;
  const role = contact?.employeeJobRole;
  const level = contact?.employeeTitle;
  const offeredTitle = contact?.employeeOfferedTitle;

  const previewContact = selectedRow?.Contact;
  const previewRole = previewContact?.employeeJobRole;
  const previewLevel = previewContact?.employeeTitle;
  const previewOffered = previewContact?.employeeOfferedTitle;

  const catalogCategoryFilterOptions = useMemo(() => {
    const rows = mapData?.catalog_applicable || [];
    const byId = new Map();
    for (const r of rows) {
      const c = r.category;
      const id = c?.id;
      if (id && !byId.has(id)) byId.set(id, c);
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [mapData?.catalog_applicable]);

  const catalogGroupedByCategory = useMemo(() => {
    const rows = mapData?.catalog_applicable || [];
    const filtered = catalogCategoryFilter
      ? rows.filter((r) => String(r.category?.id) === String(catalogCategoryFilter))
      : rows;
    const byCat = new Map();
    for (const row of filtered) {
      const cid = row.category?.id != null ? String(row.category.id) : '__none__';
      if (!byCat.has(cid)) {
        byCat.set(cid, { category: row.category, rows: [] });
      }
      byCat.get(cid).rows.push(row);
    }
    return Array.from(byCat.values()).sort((a, b) =>
      String(a.category?.name || '').localeCompare(String(b.category?.name || ''), undefined, {
        sensitivity: 'base',
      })
    );
  }, [mapData?.catalog_applicable, catalogCategoryFilter]);

  const restrictTreeSelectId = selfOnlyBrowse && myEmployeeId ? myEmployeeId : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        {selfOnlyBrowse ? 'My Employee Map' : 'Employee Map'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selfOnlyBrowse ? (
          <>
            Browse your company’s <strong>reporting tree</strong> on the left. Only <strong>your</strong> employee record
            and OKR map can be opened here — search is turned off so you cannot filter the directory to someone else’s row.
            Use <strong>OKRs → Employee Map</strong> (if you have access) for the full directory experience.
          </>
        ) : (
          <>
            Browse employees, open the <strong>record</strong> for HR details, or the <strong>detailed map</strong> to see
            how Job Role and Employee Level connect to assigned OKRs and catalog targets (weights &amp; numbers).
          </>
        )}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          {selfOnlyBrowse && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              You may browse the org chart, but you may only open <strong>your own</strong> details and OKR map. Other
              people’s rows are disabled; hover a row for a short explanation.
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            placeholder="Search name, email, or employee code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={selfOnlyBrowse}
            helperText={selfOnlyBrowse ? 'Search is not available in My Employee Map.' : undefined}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
            The <strong>org chart</strong> groups people by company (employee <strong>client account</strong> when set,
            otherwise the first <strong>linked account</strong> on the contact). Under each company, use the chevrons to
            expand or collapse branches; reporting lines are shown with a left spine like a file explorer. Each row shows{' '}
            <strong>Job role</strong> and <strong>Employee level</strong> when available. Anyone without an HR profile is
            listed separately at the bottom of that company.
          </Typography>
          {!loadingList && employees.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selfOnlyBrowse
                ? `${employees.length} employee${employees.length === 1 ? '' : 's'} in the directory (full list; search disabled).`
                : `${employees.length} employee${employees.length === 1 ? '' : 's'} match this search.`}
            </Typography>
          )}
          <Paper
            variant="outlined"
            sx={{ maxHeight: { xs: 420, md: 'min(52vh, 560px)' }, overflow: 'auto', p: 0.5 }}
          >
            {loadingList ? (
              <LinearProgress />
            ) : (
              <>
                {companyTree.map(([companyName, groupRows]) => (
                  <Accordion key={companyName} defaultExpanded disableGutters sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <BusinessIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={companyName}>
                          {companyName}
                        </Typography>
                        <Chip size="small" label={`${groupRows.length} in tree`} sx={{ height: 22 }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 0.5, pb: 1 }}>
                      <OkrCompanyReportingTree
                        rows={groupRows}
                        selectedId={selectedId}
                        onSelectRow={handleSelectEmployee}
                        restrictSelectToEmployeeId={restrictTreeSelectId}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}
            {!loadingList && employees.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No employees match your search. Try a single word or part of an email or employee code.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          {!selectedId && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <MapIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                {selfOnlyBrowse
                  ? myEmployeeId
                    ? 'Select your name in the org chart on the left (your row is the only one you can open) to view your HR summary and detailed OKR map.'
                    : 'When your login is linked to an employee HR profile, your row in the tree will be the only one you can open. Until then you can browse the directory but not open anyone’s map from this page.'
                  : 'Select someone in the org chart on the left to view their HR record, job role, level, and the detailed OKR map (catalog targets and category weights).'}
              </Typography>
            </Paper>
          )}

          {selectedId && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedRow?.Contact
                    ? `${selectedRow.Contact.first_name} ${selectedRow.Contact.last_name}`
                    : 'Employee'}
                  {selectedRow?.employee_code ? (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({selectedRow.employee_code})
                    </Typography>
                  ) : null}
                </Typography>
                {isAdmin ? (
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    component={RouterLink}
                    to={`/employees/${selectedId}`}
                  >
                    Full employee record
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Full HR record is available to administrators only.
                  </Typography>
                )}
              </Box>

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab icon={<PersonIcon />} iconPosition="start" label="Employee record" />
                <Tab icon={<MapIcon />} iconPosition="start" label="Employee detailed map" />
              </Tabs>

              {mapLoading && <LinearProgress sx={{ mb: 2 }} />}

              {tab === 0 && mapLoading && selectedRow && !String(selectedId).startsWith('pending:') && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Loading full HR and OKR map…
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Job role (OKR catalog)
                        </Typography>
                        <Typography variant="body2">{previewRole?.name || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Employee level
                        </Typography>
                        <Typography variant="body2">{previewLevel?.name || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Offered title
                        </Typography>
                        <Typography variant="body2">{previewOffered?.name || '—'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
              {tab === 0 && mapLoading && (!selectedRow || String(selectedId).startsWith('pending:')) && (
                <Typography color="text.secondary">Loading employee…</Typography>
              )}
              {tab === 0 && !mapLoading && emp && (
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">{contact?.email || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Department / team
                        </Typography>
                        <Typography variant="body2">
                          {emp.department || '—'}
                          {emp.team_name ? ` · ${emp.team_name}` : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Job title
                        </Typography>
                        <Typography variant="body2">{emp.job_title || emp.designation || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip size="small" label={emp.status || '—'} />
                          <Chip size="small" sx={{ ml: 1 }} label={emp.employment_type || '—'} variant="outlined" />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Job role (OKR catalog)
                        </Typography>
                        <Typography variant="body2">{role?.name || '—'}</Typography>
                        {role?.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {role.description}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Employee level
                        </Typography>
                        <Typography variant="body2">{level?.name || '—'}</Typography>
                        {level?.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {level.description}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Employee offered title
                        </Typography>
                        <Typography variant="body2">{offeredTitle?.name || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          System user (for OKR assignment)
                        </Typography>
                        <Typography variant="body2">
                          {emp.User
                            ? `${emp.User.first_name} ${emp.User.last_name} (${emp.User.email})`
                            : 'No login user linked — assign objectives after provisioning a user.'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {tab === 1 && mapData && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Flow: position in the catalog (role &amp; level) → expectations from the KR catalog → objectives assigned
                    to this person.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'stretch',
                      gap: 2,
                      mb: 3
                    }}
                  >
                    <MapFlowCard title="1 · Job role" subtitle={role?.name || 'Not set'} accent="primary.main">
                      <Typography variant="caption" color="text.secondary">
                        From the contact / HR catalog. Drives which catalog Key Result rows apply.
                      </Typography>
                    </MapFlowCard>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        alignSelf: 'center',
                        color: 'text.secondary',
                        flexShrink: 0,
                        gap: 0,
                      }}
                    >
                      <Box sx={{ width: { xs: 8, sm: 16 }, height: 2, bgcolor: 'divider', borderRadius: 1 }} />
                      <ArrowForwardIcon sx={{ fontSize: 28 }} />
                      <Box sx={{ width: { xs: 8, sm: 16 }, height: 2, bgcolor: 'divider', borderRadius: 1 }} />
                    </Box>
                    <MapFlowCard title="2 · Employee level" subtitle={level?.name || 'Not set'} accent="secondary.main">
                      <Typography variant="caption" color="text.secondary">
                        Level within that role. Together with role, selects target % and target numbers in the KR matrix.
                      </Typography>
                    </MapFlowCard>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        alignSelf: 'center',
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ width: { xs: 8, sm: 16 }, height: 2, bgcolor: 'divider', borderRadius: 1 }} />
                      <ArrowForwardIcon sx={{ fontSize: 28 }} />
                      <Box sx={{ width: { xs: 8, sm: 16 }, height: 2, bgcolor: 'divider', borderRadius: 1 }} />
                    </Box>
                    <MapFlowCard title="3 · OKRs &amp; targets" subtitle="Below" accent="success.main">
                      <Typography variant="caption" color="text.secondary">
                        Assigned objectives (user) + catalog lines for this role × level.
                      </Typography>
                    </MapFlowCard>
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Assigned OKRs
                  </Typography>
                  {(mapData.assigned_okrs || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {emp?.user_id
                        ? 'No objectives assigned to this employee’s user yet. Use OKR setup to create objectives and assign this user.'
                        : 'No system user on this employee — link a user to assign OKRs.'}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                      {(mapData.assigned_okrs || []).map((okr) => (
                        <Card key={okr.id} variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {okr.title}
                              </Typography>
                              <Button size="small" onClick={() => navigate(`/okr/objectives/${okr.id}`)}>
                                Open
                              </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {okr.category?.name || 'Category'} · {okr.status || '—'} · score {n(okr.computed_score).toFixed(1)}%
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            {(okr.keyResults || []).map((kr) => (
                              <Box key={kr.id} sx={{ mb: 0.5 }}>
                                <Typography variant="body2">
                                  <strong>{kr.kr_code}</strong> — {kr.title}{' '}
                                  <Chip
                                    component="span"
                                    size="small"
                                    label={`${n(kr.weight_percent)}% wt · ${n(kr.progress_percent)}% done`}
                                    sx={{ ml: 1, height: 22 }}
                                  />
                                </Typography>
                              </Box>
                            ))}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Catalog alignment (this role × level)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Rows from <strong>KR+Cat(%)+Target(Nos)</strong> that have a target for this job role and employee level.
                    The optional <strong>Category OKR wt</strong> chip is the category’s <strong>portfolio weight</strong> from{' '}
                    <strong>OKR setup → Key Result Categories</strong> (defaults to 0% until you set it — it is not the same
                    as matrix target % on each line).
                  </Typography>
                  {(mapData.catalog_applicable || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {role && level
                        ? 'No catalog Key Results include targets for this role and level yet. Configure the catalog matrix for this combination.'
                        : 'Set Job Role and Employee Level on the contact to see catalog expectations.'}
                    </Typography>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                          <InputLabel id="okr-map-catalog-cat-filter">Catalog category</InputLabel>
                          <Select
                            labelId="okr-map-catalog-cat-filter"
                            label="Catalog category"
                            value={catalogCategoryFilter}
                            onChange={(e) => setCatalogCategoryFilter(e.target.value)}
                          >
                            <MenuItem value="">All categories</MenuItem>
                            {catalogCategoryFilterOptions.map((c) => (
                              <MenuItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary">
                          Targets use the same accent as the category. Rows are grouped by category below.
                        </Typography>
                      </Box>
                      {catalogGroupedByCategory.map(({ category, rows: catRows }) => {
                        const accent = okrCategoryColor(category);
                        return (
                          <Box key={category?.id != null ? String(category.id) : '__none__'} sx={{ mb: 2.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 4,
                                  alignSelf: 'stretch',
                                  minHeight: 28,
                                  borderRadius: 1,
                                  bgcolor: accent || 'grey.400',
                                }}
                              />
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, color: accent || 'text.primary' }}
                              >
                                {category?.name || 'Uncategorized'}
                              </Typography>
                              {n(category?.weight_percent) > 0 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Category OKR wt ${n(category.weight_percent).toFixed(1)}%`}
                                  sx={{
                                    height: 24,
                                    ...(accent
                                      ? {
                                          color: accent,
                                          borderColor: accent,
                                          borderWidth: 1,
                                          borderStyle: 'solid',
                                        }
                                      : {}),
                                  }}
                                />
                              )}
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>KR ID</TableCell>
                                    <TableCell>Key Result</TableCell>
                                    <TableCell align="right">Matrix target %</TableCell>
                                    <TableCell align="right">Target #</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {catRows.flatMap((row) =>
                                    (row.targets || []).map((t, i) => (
                                      <TableRow key={`${row.id}-${t.id || i}`}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.kr_code}</TableCell>
                                        <TableCell>
                                          {row.name}
                                          {row.description && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              {row.description}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontWeight: 700,
                                            color: accent || 'text.primary',
                                          }}
                                        >
                                          {n(t.target_percent).toFixed(1)}%
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            fontWeight: 700,
                                            color: accent || 'text.primary',
                                          }}
                                        >
                                          {n(t.target_number)}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
